<script lang="ts">
	import { onMount } from 'svelte';
	import { goto } from '$app/navigation';
	import { navigating, page } from '$app/stores';
	import { Button } from '$lib/components/ui/button';
	import { Input } from '$lib/components/ui/input';
	import * as Sheet from '$lib/components/ui/sheet';
	import * as DropdownMenu from '$lib/components/ui/dropdown-menu';
	import { Menu, User, LogOut, Mail, Search, X, Loader2, LogIn, LayoutDashboard } from '@lucide/svelte';
	import Logo from './logo.svelte';
	import TheUploadButton from '../upload/TheUploadButton.svelte';
	import { isEmployerPage } from './footer-links';
	import { isDashboardAuthenticated } from '$lib/utils/auth-cookie';
	import { PUBLIC_DASHBOARD_URL } from '$env/static/public';

	let { variant = 'default' }: { variant?: 'default' | 'simple' } = $props();

	const DASHBOARD_URL = PUBLIC_DASHBOARD_URL || 'https://app.talentprofile.net';
	const SEARCH_DEBOUNCE_MS = 500;

	let mounted = $state(false);
	let mobile_menu_open = $state(false);
	let current_path = $derived($page.url.pathname);
	let auth_data = $state<{ type: 'cv' | 'jd'; id: string; token: string; email?: string } | null>(
		null
	);
	let dashboardAuth = $state(false);

	let isJobsPage = $derived(
		$page.url.pathname === '/jobs' || $page.url.pathname.startsWith('/jobs/')
	);

	let headerSearchValue = $state('');
	let headerSearchTimeout = $state<ReturnType<typeof setTimeout> | undefined>();

	$effect(() => {
		if (isJobsPage) {
			headerSearchValue = $page.url.searchParams.get('search') || '';
		}
	});

	$effect(() => {
		if (!isJobsPage) {
			headerSearchValue = '';
		}
	});

	$effect(() => {
		return () => {
			clearTimeout(headerSearchTimeout);
		};
	});

	function applyHeaderSearch(search: string) {
		if (isJobsPage) {
			const url = new URL($page.url);
			if (!search) {
				url.searchParams.delete('search');
			} else {
				url.searchParams.set('search', search);
			}
			url.searchParams.delete('page');
			goto(url.toString(), { noScroll: true });
		} else {
			if (search) {
				goto(`/jobs?search=${encodeURIComponent(search)}`);
			} else {
				goto('/jobs');
			}
		}
	}

	function handleHeaderSearchInput() {
		clearTimeout(headerSearchTimeout);
		headerSearchTimeout = setTimeout(() => {
			applyHeaderSearch(headerSearchValue);
		}, SEARCH_DEBOUNCE_MS);
	}

	function handleHeaderSearchSubmit(e: SubmitEvent) {
		e.preventDefault();
		clearTimeout(headerSearchTimeout);
		applyHeaderSearch(headerSearchValue);
	}

	function clearHeaderSearch() {
		headerSearchValue = '';
		clearTimeout(headerSearchTimeout);
		if (isJobsPage) {
			applyHeaderSearch('');
		}
	}

	const upload_button_type = $derived<'cv' | 'jd'>(
		isEmployerPage($page.url.pathname) ? 'jd' : 'cv'
	);

	const nav_links = [
		{ href: '/jobs', label: 'Browse Jobs', showFor: ['default'] },
		{ href: '/hire', label: 'For Employers', showFor: ['default'] },
	];

	const visible_links = $derived(nav_links.filter((link) => link.showFor.includes(variant)));

	onMount(() => {
		load_auth_data();
		dashboardAuth = isDashboardAuthenticated();
		mounted = true;

		const handle_storage = (e: StorageEvent) => {
			if (e.key === 'guestProfileData') {
				load_auth_data();
			}
		};

		window.addEventListener('storage', handle_storage);

		const handle_auth = () => load_auth_data();
		window.addEventListener('auth-changed', handle_auth);

		return () => {
			window.removeEventListener('storage', handle_storage);
			window.removeEventListener('auth-changed', handle_auth);
		};
	});

	function load_auth_data() {
		const stored = localStorage.getItem('guestProfileData');
		if (stored) {
			try {
				const parsed = JSON.parse(stored);
				if (parsed?.id && parsed?.token) {
					auth_data = parsed;
				} else {
					auth_data = null;
				}
			} catch (e) {
				console.error('Failed to parse auth data:', e);
				auth_data = null;
			}
		} else {
			auth_data = null;
		}
	}

	function handle_logout() {
		localStorage.removeItem('guestProfileData');
		auth_data = null;
		window.dispatchEvent(new Event('auth-changed'));
	}
</script>

<header class="sticky top-0 z-50 border-b bg-white/80 backdrop-blur-sm">
	<div class="container mx-auto flex items-center justify-between px-4 py-4">
		<a href="/" class="flex flex-shrink-0 items-center space-x-2">
			<Logo />
			<span class="hidden text-xl font-bold text-gray-900 md:inline">
				Talent<strong>Profile</strong>
			</span>
		</a>

		<form
			onsubmit={handleHeaderSearchSubmit}
			class="relative mx-3 min-w-0 flex-1 md:hidden"
		>
			{#if $navigating}
				<Loader2
					class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-blue-500"
				/>
			{:else}
				<Search
					class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
				/>
			{/if}
			<Input
				type="text"
				placeholder="Search jobs..."
				bind:value={headerSearchValue}
				oninput={handleHeaderSearchInput}
				class="h-9 pl-9 text-sm {headerSearchValue ? 'pr-8' : ''}"
			/>
			{#if headerSearchValue}
				<button
					type="button"
					onclick={clearHeaderSearch}
					class="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					aria-label="Clear search"
				>
					<X class="h-3.5 w-3.5" />
				</button>
			{/if}
		</form>

		<nav class="hidden items-center space-x-6 md:flex">
			{#each visible_links as link (link.href)}
				<a
					href={link.href}
					class="text-sm whitespace-nowrap transition-colors {current_path === link.href ||
					(link.href === '/blog' && current_path.startsWith('/blog'))
						? 'font-semibold text-gray-900'
						: 'text-gray-600 hover:text-gray-900'}"
				>
					{link.label}
				</a>
			{/each}

			{#if variant === 'default' && mounted}
				{#if dashboardAuth}
					<a
						href={DASHBOARD_URL}
						target="_blank"
						rel="noopener noreferrer"
						class="text-sm font-medium text-blue-600 transition-colors hover:text-blue-700"
					>
						Dashboard
					</a>
				{:else if auth_data}
					<DropdownMenu.Root>
						<DropdownMenu.Trigger>
							<Button variant="ghost" size="sm" class="gap-2">
								<User class="h-4 w-4" />
								<span class="hidden sm:inline">
									{auth_data?.type === 'cv' ? 'My Profile' : 'My Jobs'}
								</span>
							</Button>
						</DropdownMenu.Trigger>
						<DropdownMenu.Content align="end" class="w-56">
							<DropdownMenu.Label>
								<div class="flex flex-col space-y-1">
									<p class="text-sm font-medium">
										{auth_data.type === 'cv' ? 'Talent' : 'Employer'}
									</p>
									{#if auth_data.email}
										<p class="text-xs text-gray-500">{auth_data.email}</p>
									{/if}
								</div>
							</DropdownMenu.Label>
							<DropdownMenu.Separator />

							{#if auth_data.type === 'cv'}
								<DropdownMenu.Item>
									<a href="/preferences/{auth_data?.token}" class="flex items-center gap-2">
										<Mail class="h-4 w-4" />
										<span>Email Preferences</span>
									</a>
								</DropdownMenu.Item>
							{:else}
								<DropdownMenu.Item>
									<Mail class="mr-2 h-4 w-4" />
									<span>Manage Jobs</span>
								</DropdownMenu.Item>
							{/if}

							<DropdownMenu.Separator />

							<DropdownMenu.Item>
								<a
									href="{DASHBOARD_URL}/sign-up"
									target="_blank"
									rel="noopener noreferrer"
									class="flex items-center gap-2"
								>
									<User class="h-4 w-4" />
									<span>Create Account</span>
								</a>
							</DropdownMenu.Item>

							<DropdownMenu.Separator />
							<DropdownMenu.Item onclick={handle_logout} class="text-red-600">
								<LogOut class="mr-2 h-4 w-4" />
								<span>Log Out</span>
							</DropdownMenu.Item>
						</DropdownMenu.Content>
					</DropdownMenu.Root>
				{:else}
					<a
						href="{DASHBOARD_URL}/sign-in"
						target="_blank"
						rel="noopener noreferrer"
						class="text-sm text-gray-600 transition-colors hover:text-gray-900"
					>
						Sign In
					</a>
					{#key upload_button_type}
						<TheUploadButton type={upload_button_type} />
					{/key}
				{/if}
			{/if}
		</nav>

		<Sheet.Root bind:open={mobile_menu_open}>
			<Sheet.Trigger class="md:hidden">
				{#snippet child({ props }: { props: Record<string, unknown> })}
					<Button
						variant="ghost"
						class="flex-shrink-0 md:hidden"
						size="sm"
						aria-label="Open navigation menu"
						{...props}
					>
						<Menu class="h-6 w-6" />
					</Button>
				{/snippet}
			</Sheet.Trigger>

			<Sheet.Content side="right" class="w-[300px] px-8 sm:w-[400px]">
				<Sheet.Header>
					<Sheet.Title>Menu</Sheet.Title>
				</Sheet.Header>

				<nav class="mt-8 flex flex-col space-y-4">
					{#if mounted && dashboardAuth}
						<a
							href={DASHBOARD_URL}
							target="_blank"
							rel="noopener noreferrer"
							class="flex items-center gap-2 text-lg font-medium text-blue-600 transition-colors hover:text-blue-700"
							onclick={() => (mobile_menu_open = false)}
						>
							<LayoutDashboard class="h-5 w-5" />
							Dashboard
						</a>
						<div class="border-t pt-4"></div>
					{:else if mounted && auth_data}
						<div class="rounded-lg bg-blue-50 p-4">
							<div class="mb-2 flex items-center gap-2">
								<User class="h-5 w-5 text-blue-600" />
								<span class="font-medium text-blue-900">
									{auth_data.type === 'cv' ? 'Job Seeker' : 'Employer'}
								</span>
							</div>
							{#if auth_data.email}
								<p class="text-sm text-blue-700">{auth_data.email}</p>
							{/if}
						</div>

						{#if auth_data.type === 'cv'}
							<a
								href="/preferences/{auth_data.token}"
								class="text-lg text-gray-600 transition-colors hover:text-gray-900"
							>
								Email Preferences
							</a>
						{:else}
							<a
								href="/manage-jobs"
								class="text-lg text-gray-600 transition-colors hover:text-gray-900"
								onclick={() => (mobile_menu_open = false)}
							>
								Manage Jobs
							</a>
						{/if}

						<a
							href="{DASHBOARD_URL}/sign-up"
							target="_blank"
							rel="noopener noreferrer"
							class="text-lg text-blue-600 transition-colors hover:text-blue-700"
							onclick={() => (mobile_menu_open = false)}
						>
							Create Account
						</a>

						<button
							onclick={() => {
								handle_logout();
								mobile_menu_open = false;
							}}
							class="flex items-center gap-2 text-lg text-red-600 transition-colors hover:text-red-700"
						>
							<LogOut class="h-5 w-5" />
							<span>Log Out</span>
						</button>

						<div class="border-t pt-4"></div>
					{/if}

					{#each visible_links as link (link.href)}
						<a
							href={link.href}
							class="text-lg transition-colors {current_path === link.href
								? 'font-semibold text-gray-900'
								: 'text-gray-600 hover:text-gray-900'}"
							onclick={() => (mobile_menu_open = false)}
						>
							{link.label}
						</a>
					{/each}

					{#if variant === 'default' && mounted && !auth_data && !dashboardAuth}
						<div class="border-t pt-4 space-y-3">
							<a
								href="{DASHBOARD_URL}/sign-in"
								target="_blank"
								rel="noopener noreferrer"
								class="flex items-center gap-2 text-lg text-gray-600 transition-colors hover:text-gray-900"
								onclick={() => (mobile_menu_open = false)}
							>
								<LogIn class="h-5 w-5" />
								Sign In
							</a>
							{#key upload_button_type}
								<TheUploadButton type={upload_button_type} />
							{/key}
						</div>
					{/if}
				</nav>
			</Sheet.Content>
		</Sheet.Root>
	</div>
</header>